import {CustomerAtDeleted} from "../../../config/view_const/customer";
import {Status, WorkIntention} from "../../../domain/model/customer";
import {FrontDate} from "../../../domain/model/frontDate";
import {SearchHighlight} from "../../../domain/model/search_highlight";
import {MultiSkillTag, OccupationSkillTag, SingleSkillTag,} from "../../../domain/model/skill_tag";
import {entity} from "../rpc/api";
import SearchHighlightAtServer = entity.SearchHighlight;

class CustomerConvertResponse {
    public static from(from: entity.Customer): CustomerConvertResponse {
        // Backend => FrontEnd における型変換
        const birthdateDate: FrontDate = FrontDate.from(from.birthdate);
        const createdAtDate: FrontDate = FrontDate.from(from.createdAt);
        const interviewUpdatedAtDate: FrontDate = FrontDate.from(
            from.interviewUpdatedAt
        );

        // 退会時のname変更
        const name: string =
            from.status === Status.Status_Delete ? CustomerAtDeleted.name : from.name;

        const highlights: SearchHighlight[] = from.highlights.map(
            (highlight: SearchHighlightAtServer) => {
                return SearchHighlight.from(highlight);
            }
        );

        const singleSkillTags: SingleSkillTag[] = from.singleSkillTags.map(
            (singleSkillTag: entity.SingleSkillTag) => {
                return SingleSkillTag.from(singleSkillTag);
            }
        );

        const multiSkillTags: MultiSkillTag[] = from.multiSkillTags.map(
            (multiSkillTag: entity.MultiSkillTag) => {
                return MultiSkillTag.from(multiSkillTag);
            }
        );

        const occupationSkillTags: OccupationSkillTag[] =
            from.occupationSkillTags.map(
                (occupationSkillTag: entity.OccupationSkillTag) => {
                    return OccupationSkillTag.from(occupationSkillTag);
                }
            );

        return new CustomerConvertResponse(
            from.id,
            name,
            from.nameKana,
            from.gsIconURL,
            from.signedIconURL,
            birthdateDate,
            from.gender,
            from.phoneNumber,
            from.email,
            from.pr,
            from.address,
            from.academicBackgrounds,
            from.requirements,
            from.languages,
            from.moreLanguage,
            from.speakingLevel,
            from.status,
            from.resumeURL,
            from.jobHistoryURL,
            from.workingFrequency,
            from.requestedFee,
            from.requestedCondition,
            from.dutySummary,
            from.catchphrase,
            createdAtDate,
            highlights,
            from.workIntention,
            from.adminMemo,
            from.interviewMemo,
            interviewUpdatedAtDate,
            from.fullTimePossibility,
            from.brokerageProjectPossibility,
            from.jobCareerCatchphrase,
            singleSkillTags,
            multiSkillTags,
            occupationSkillTags,
            from.portfolioURL,
            from.displayOccupationSkillCategories,
            from.displayOccupationSkillTags,
            from.type,
            from.birthplace,
            from.facebookURL,
            from.linkedInURL,
            from.xURL,
            ""
        );
    }

    public static fromSafe(from: entity.SafeCustomer): CustomerConvertResponse {
        // Backend => FrontEnd における型変換
        const birthdateDate: FrontDate = FrontDate.from(from.birthdate);
        const createdAtDate: FrontDate = FrontDate.from(from.createdAt);

        // 退会時のname変更
        const name: string =
            from.status === Status.Status_Delete ? CustomerAtDeleted.name : from.name;

        const highlights: SearchHighlight[] = from.highlights.map(
            (highlight: SearchHighlightAtServer) => {
                return SearchHighlight.from(highlight);
            }
        );

        const singleSkillTags: SingleSkillTag[] = from.singleSkillTags.map(
            (singleSkillTag: entity.SingleSkillTag) => {
                return SingleSkillTag.from(singleSkillTag);
            }
        );

        const multiSkillTags: MultiSkillTag[] = from.multiSkillTags.map(
            (multiSkillTag: entity.MultiSkillTag) => {
                return MultiSkillTag.from(multiSkillTag);
            }
        );

        const occupationSkillTags: OccupationSkillTag[] =
            from.occupationSkillTags.map(
                (occupationSkillTag: entity.OccupationSkillTag) => {
                    return OccupationSkillTag.from(occupationSkillTag);
                }
            );

        return new CustomerConvertResponse(
            from.id,
            name,
            "",
            from.gsIconURL,
            from.signedIconURL,
            birthdateDate,
            from.gender,
            "",
            "",
            from.pr,
            from.address,
            from.academicBackgrounds,
            from.requirements,
            from.languages,
            from.moreLanguage,
            from.speakingLevel,
            from.status,
            from.resumeURL,
            from.jobHistoryURL,
            from.workingFrequency,
            from.requestedFee,
            from.requestedCondition,
            from.dutySummary,
            from.catchphrase,
            createdAtDate,
            highlights,
            from.workIntention,
            from.adminMemo,
            "",
            FrontDate.fromEmpty(),
            from.fullTimePossibility,
            from.brokerageProjectPossibility,
            from.jobCareerCatchphrase,
            singleSkillTags,
            multiSkillTags,
            occupationSkillTags,
            from.portfolioURL,
            from.displayOccupationSkillCategories,
            from.displayOccupationSkillTags,
            from.type,
            from.birthplace,
            "",
            "",
            "",
            from.agenticSummary,
            from.ageGroup
        );
    }

    constructor(
        public readonly id: string,
        public readonly name: string,
        public readonly nameKana: string,
        public readonly gsIconURL: string,
        public readonly signedIconURL: string,
        public readonly birthdate: FrontDate,
        public readonly gender: entity.User.Gender,
        public readonly phoneNumber: string,
        public readonly email: string,
        public readonly pr: string,
        public readonly address: string,
        public readonly academicBackgrounds: string[],
        public readonly requirements: string[],
        public readonly languages: entity.Customer.Language[],
        public readonly moreLanguage: string,
        public readonly speakingLevel: entity.Customer.SpeakingLevel,
        public readonly status: entity.Customer.Status,
        public readonly resumeURL: string,
        public readonly jobHistoryURL: string,
        public readonly workingFrequency: entity.Customer.WorkingFrequency,
        public readonly requestedFee: entity.Customer.RequestedFee,
        public readonly requestedCondition: string,
        public readonly dutySummary: string,
        public readonly catchphrase: string,
        public readonly createdAt: FrontDate,
        public readonly highlights: SearchHighlight[],
        public readonly workIntention: WorkIntention,
        public readonly adminMemo: string,
        public readonly interviewMemo: string,
        public readonly interviewUpdatedAt: FrontDate,
        public readonly fullTimePossibility: entity.Customer.FullTimePossibility,
        public readonly brokerageProjectPossibility: entity.Customer.BrokerageProjectPossibility,
        public readonly jobCareerCatchphrase: string,
        public readonly singleSkillTags: SingleSkillTag[],
        public readonly multiSkillTags: MultiSkillTag[],
        public readonly occupationSkillTags: OccupationSkillTag[],
        public readonly portfolio: string,
        public readonly displayOccupationSkillCategories: string[],
        public readonly displayOccupationSkillTags: string[],
        public readonly type: entity.Customer.Type,
        public readonly birthplace: string,
        public readonly facebookURL: string,
        public readonly linkedInURL: string,
        public readonly xURL: string,
        // AgenticSearchで返却されるLLMによる要約PR
        public readonly agenticSummary: string,
        public readonly ageGroup: string = ""
    ) {
    }
}

class CustomerConvertResponseList {
    public static from(
        from: entity.SafeCustomerList
    ): CustomerConvertResponseList {
        const items: CustomerConvertResponse[] = [];

        from.items.map((item, index) => {
            return items.push(
                CustomerConvertResponse.fromSafe(item as entity.SafeCustomer)
            );
        });

        return new CustomerConvertResponseList(items);
    }

    constructor(public readonly items: CustomerConvertResponse[]) {
    }
}

export {CustomerConvertResponse, CustomerConvertResponseList};



// WEBPACK FOOTER //
// ./src/infra/api/service/customer_api_convert.ts